'use client';

import { useRef, useState } from 'react';
import { upload } from '@vercel/blob/client';
import {
  SIZE_CAPS,
  FORUM_LIMITS,
  kindForContentType,
  sanitizeFilename,
  humanSize,
} from '@/lib/forumConstants';
import type { UIAttachment } from './types';

interface PendingUpload {
  id: string;
  name: string;
  progress: number;
  error?: string;
}

// Drag-drop / picker that uploads files directly to Vercel Blob and reports the
// resulting attachment metadata to the parent. Bypasses the serverless body
// limit (files never pass through our routes — only their metadata does).
export default function AttachmentInput({
  attachments,
  onChange,
  onLoginNeeded,
}: {
  attachments: UIAttachment[];
  onChange: (next: UIAttachment[]) => void;
  onLoginNeeded?: () => void;
}) {
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);

    for (const file of list) {
      if (attachments.length + pending.length >= FORUM_LIMITS.maxAttachments) {
        alert(`You can attach at most ${FORUM_LIMITS.maxAttachments} files.`);
        break;
      }
      const kind = kindForContentType(file.type || '');
      if (file.size > SIZE_CAPS[kind]) {
        alert(`${file.name} is too large (max ${humanSize(SIZE_CAPS[kind])} for ${kind}s).`);
        continue;
      }

      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setPending((p) => [...p, { id: tempId, name: file.name, progress: 0 }]);

      try {
        const blob = await upload(sanitizeFilename(file.name), file, {
          access: 'public',
          handleUploadUrl: '/api/upload',
          contentType: file.type || undefined,
          clientPayload: JSON.stringify({ kind }),
          onUploadProgress: (e) =>
            setPending((p) =>
              p.map((x) => (x.id === tempId ? { ...x, progress: e.percentage } : x))
            ),
        });

        onChange([
          ...attachments,
          {
            url: blob.url,
            pathname: blob.pathname,
            name: file.name,
            contentType: file.type || '',
            size: file.size,
            kind,
          },
        ]);
        setPending((p) => p.filter((x) => x.id !== tempId));
      } catch (err: any) {
        console.error(err);
        const msg: string = err?.message || '';
        if (msg.toLowerCase().includes('401') || msg.toLowerCase().includes('logged in')) {
          onLoginNeeded?.();
        }
        setPending((p) =>
          p.map((x) => (x.id === tempId ? { ...x, error: 'Failed' } : x))
        );
        setTimeout(() => setPending((p) => p.filter((x) => x.id !== tempId)), 2500);
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (idx: number) => {
    onChange(attachments.filter((_, i) => i !== idx));
  };

  return (
    <div className="forum-uploader">
      <div
        className={`forum-dropzone ${dragOver ? 'drag' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <span className="forum-dropzone-title">Drag &amp; drop files, or click to browse</span>
        <span className="forum-dropzone-sub">
          Images, video, or code/text files (lua, js, txt, anything) — up to{' '}
          {FORUM_LIMITS.maxAttachments} files
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {(attachments.length > 0 || pending.length > 0) && (
        <div className="forum-attach-chips">
          {attachments.map((a, i) => (
            <div key={a.url} className="forum-attach-chip">
              <span className="forum-attach-kind">{a.kind}</span>
              <span className="forum-attach-name" title={a.name}>
                {a.name}
              </span>
              <span className="forum-attach-size">{humanSize(a.size)}</span>
              <button type="button" className="forum-attach-remove" onClick={() => removeAt(i)} title="Remove">
                ✕
              </button>
            </div>
          ))}
          {pending.map((p) => (
            <div key={p.id} className="forum-attach-chip pending">
              <span className="forum-attach-name">{p.name}</span>
              <span className="forum-attach-size">
                {p.error ? p.error : `${Math.round(p.progress)}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
